/**
 * Monitors the build version by polling `/api/build-info` every 30 seconds.
 * If a new build is detected (based on `buildEndDate`), it reloads the page.
 * It also updates a DOM element with ID `#credentional` to reflect build times.
 * 
 * This is intended to ensure users always operate on the latest deployed version
 * without relying solely on route-based middleware.
 */

import { useEventListener } from '@vueuse/core'

export function useBuildWatcher() {
  const { useCustomCookie } = useCustomTools()
  const { $dayjs } = useNuxtApp()

  // Initialize useState once
  const buildDateState = useState('buildDate')
  const buildDateCookie = useCustomCookie('buildDate')

  // Determine if this is the first visit (no cookie and no state)
  const isFirstVisit = !buildDateCookie.value && !buildDateState.value

  // Synchronize: if cookie exists, update state; if not, update cookie from state
  if (buildDateCookie.value && buildDateState.value !== buildDateCookie.value) {
    buildDateState.value = buildDateCookie.value
  } else if (!buildDateCookie.value && buildDateState.value) {
    buildDateCookie.value = buildDateState.value
  }

  let interval = null
  let cleanupReload = null

  const updateBuildInfo = async () => {
    try {
      const buildData = await $fetch(`/api/build-info?timestamp=${Date.now()}`, {
        method: 'GET'
      })

      if (!buildData) return

      const newDate = new Date(buildData.buildEndDate).getTime()

      /*
      * NOTE: Temporarily disabling in favor of logic in plugins/errors.js
      */
      const current = buildDateState.value ? new Date(buildDateState.value).getTime() : 0

      // If this is the first visit, just save the date, do not reload
      if (isFirstVisit) {
        buildDateState.value = newDate
        buildDateCookie.value = newDate
        return
      }

      if (current < newDate) {
        // Update both storages
        buildDateState.value = newDate
        buildDateCookie.value = newDate

        // Wait for the first user action to reload
        const performReload = () => {
          setTimeout(() => window.location.reload(true), 500)
        }
        if (!cleanupReload) {
          cleanupReload = useEventListener(
            document,
            ['mousemove', 'scroll', 'click', 'keydown', 'touchstart'],
            () => {
              performReload()
              cleanupReload()
              cleanupReload = null
            },
            { passive: true }
          )
        }
      } else {
        // Update both storages if nothing changed
        buildDateState.value = newDate
        buildDateCookie.value = newDate
      }

      // Build timing info in DOM
      const diffTime = (newDate - new Date(buildData.buildStartDate).getTime()) / 1000
      const h = Math.floor(diffTime / 3600)
      const m = Math.floor((diffTime % 3600) / 60)
      const s = Math.floor(diffTime % 60)

      if (document.querySelector('#credentional')) {
        document.querySelector('#credentional').innerHTML = `
          <!-- Build Start: ${$dayjs(buildData.buildStartDate).format('YYYY-MM-DD, HH:mm:ss')} -->
          <!-- Build End: ${$dayjs(buildData.buildEndDate).format('YYYY-MM-DD, HH:mm:ss')} -->
          <!-- Build Time (hh:mm:ss): ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} -->
        `
      }
    } catch (err) {
      console.error('Build info fetch failed:', err)
    }
  }

  // Initial call on mount
  updateBuildInfo()

  // Repeat check every 30 seconds
  if (!interval) {
    interval = setInterval(updateBuildInfo, 30000)
  }

  // Cleanup on component unmount
  onUnmounted(() => {
    if (interval) clearInterval(interval)
    interval = null
    if (cleanupReload) cleanupReload()
    cleanupReload = null
  })
}